/*Copyright (c) 2025 por Tecnologías de Información MALMUR y empresas afiliadas. Todos los derechos reservados.
Este software es información confidencial y propietaria de Copyright (c) 2025 por Tecnologías de Información MALMUR
 y sus afiliadas. ("Información Confidencial"). Usted no puede divulgar dicha Información Confidencial, 
 y solo puede usar dicha Información Confidencial de acuerdo con los términos del acuerdo de licencia que celebró con MALMUR.*/

/*Copyright (c) 2025 by Tecnologías de Información MALMUR and affiliated companies. All Rights Reserved.
This software is confidential and proprietary information of Copyright (c) 2025 by Tecnologías de Información 
MALMUR and its affiliates. ("Confidential Information"). You may not disclose such Confidential Information, 
and may only use such Confidential Information in accordance with the terms of the license agreement you entered 
into with MALMUR.*/



import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { RoutesConstants, UserRolesEnum } from '@malmur-technologies/medicalsystem-shared';
import { selectUserRoles } from 'store/slices/auth.slice';

/**
 * Props for AdminRouteGuard component.
 */
interface AdminRouteGuardProps {
  children: React.ReactNode;
  redirectTo?: string;
}

/**
 * Route guard component restricting access to admin and root users only.
 * 
 * Security Features:
 * - Checks if user has ADMIN or ROOT role
 * - Redirects non-admin users to dashboard (or custom redirect path)
 * - Returns null immediately if access denied (prevents flash of content)
 * - Uses replace navigation to prevent back-button bypass
 * - Executes redirect check on component mount and role changes
 * 
 * Used to protect sensitive pages like activity logs, system configuration,
 * and user management from unauthorized access.
 * 
 * Part of the role-based access control (RBAC) system for ISO-27001 compliance.
 * 
 * @param children - Protected admin content to render
 * @param redirectTo - Custom redirect path (default: dashboard)
 * @returns Admin content if authorized, null with redirect otherwise
 */
const AdminRouteGuard: React.FC<AdminRouteGuardProps> = ({ 
  children, 
  redirectTo = RoutesConstants.DASHBOARD 
}) => {
  const userRoles = useSelector(selectUserRoles);
  const navigate = useNavigate();

  const isAdminOrRoot = userRoles.includes(UserRolesEnum.ADMIN) || userRoles.includes(UserRolesEnum.ROOT);

  useEffect(() => {
    if (!isAdminOrRoot) {
      navigate(redirectTo, { replace: true });
    }
  }, [isAdminOrRoot, navigate, redirectTo]);

  // If user doesn't have admin/root access, don't render children
  if (!isAdminOrRoot) {
    return null;
  }

  return <>{children}</>;
};

export default AdminRouteGuard;
